import {Component, jsx} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

// simple random number helper
const rand = (a, b) => a + Math.random() * (b - a);

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  opacity: number;
  radius: number;
  colorIdx: number;
}

enum Mode {
  Luv = `luv`,
  Winter = `winter`,
}

const NUM_PARTICLES = {
  [Mode.Luv]: 50,
  [Mode.Winter]: 100,
};

const PARTICLE_COLORS = [`#fff`, `#FFB6C1`, `#FF69B4`];

export class FestiveModeHeaderAnim extends Component<EmptyObj> {
  private ctx: CanvasRenderingContext2D;
  private animCanvas: HTMLCanvasElement;
  private rafId: number;
  private particles: Particle[];
  private width: number;
  private height: number;
  private mouseXWind = 0;
  private mode = Mode.Luv;

  get config() {
    return {
      template: () => jsx(`canvas`, {}),
      helpers: {},
    };
  }

  connectedCallback() {
    super.connectedCallback();

    // this bound since they are callbacks
    this.renderTick = this.renderTick.bind(this);
    this.updateSize = this.updateSize.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);

    window.addEventListener(`resize`, this.updateSize);
    document.body.addEventListener(`mousemove`, this.handleMouseMove);

    this.animCanvas = this.el.querySelector(`canvas`);
    this.ctx = this.animCanvas.getContext(`2d`);
    this.updateSize();
    this.generateParticles();
    this.renderTick();
  }

  disconnectedCallback() {
    window.removeEventListener(`resize`, this.updateSize);
    document.body.removeEventListener(`mousemove`, this.handleMouseMove);
    cancelAnimationFrame(this.rafId);
  }

  updateSize() {
    this.width = this.animCanvas.width = this.animCanvas.clientWidth;
    this.height = this.animCanvas.height = this.animCanvas.clientHeight;
  }

  handleMouseMove(ev: MouseEvent) {
    this.mouseXWind = ev.clientX / this.width - 0.5;
  }

  generateParticles() {
    this.particles = [];
    for (let i = 0; i < NUM_PARTICLES[this.mode]; i++) {
      this.particles.push(this.makeParticle());
    }
  }

  makeParticle(): Particle {
    switch (this.mode) {
      case Mode.Winter:
        return {
          x: rand(0, this.width),
          y: rand(0, -this.height),
          vy: rand(0.1, 0.5),
          vx: rand(-0.5, 0.5),
          radius: rand(1, 3),
          opacity: rand(0.1, 0.5),
          colorIdx: 0,
        };
      case Mode.Luv:
        return {
          x: rand(0, this.width),
          y: rand(0, -this.height),
          vy: rand(0.1, 0.3),
          vx: rand(-0.5, 0.5),
          radius: rand(2, 10),
          opacity: rand(0.3, 0.7),
          colorIdx: Math.ceil(rand(1, 2)),
        };
    }
  }

  // borrowed from: http://www.java2s.com/Tutorials/Javascript/Canvas_How_to/Shape/Draw_Spade_Heart_Club_Diamond.htm
  drawHeart(x, y, width, height) {
    const ctx = this.ctx;
    ctx.beginPath();
    const topCurveHeight = height * 0.3;
    ctx.moveTo(x, y + topCurveHeight);

    // top left curve
    ctx.bezierCurveTo(x, y, x - width / 2, y, x - width / 2, y + topCurveHeight);

    // bottom left curve
    ctx.bezierCurveTo(
      x - width / 2,
      y + (height + topCurveHeight) / 2,
      x,
      y + (height + topCurveHeight) / 2,
      x,
      y + height,
    );

    // bottom right curve
    ctx.bezierCurveTo(
      x,
      y + (height + topCurveHeight) / 2,
      x + width / 2,
      y + (height + topCurveHeight) / 2,
      x + width / 2,
      y + topCurveHeight,
    );

    // top right curve
    ctx.bezierCurveTo(x + width / 2, y, x, y, x, y + topCurveHeight);

    ctx.closePath();
  }

  renderTick() {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);

    for (const particle of this.particles) {
      ctx.fillStyle = PARTICLE_COLORS[particle.colorIdx];
      particle.y += particle.vy;
      particle.x += particle.vx + this.mouseXWind;

      ctx.globalAlpha = particle.opacity;
      ctx.beginPath();
      if (this.mode === Mode.Winter) {
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      } else if (this.mode === Mode.Luv) {
        this.drawHeart(particle.x, particle.y, particle.radius * 2, particle.radius * 2);
      }
      ctx.closePath();
      ctx.fill();

      // this part makes the animation loop over and over. unclear whether we will keep this for later
      // iterations, especially if we have a sticky header in place, so commented out for now
      // if (particle.y > this.height) {
      //   Object.assign(particle, this.makeParticle());
      // }
    }

    this.rafId = requestAnimationFrame(this.renderTick);
  }
}

export const FestiveModeHeaderAnimEl = defineAndMakeJsxFactory(`mp-festive-mode-header-anim`, FestiveModeHeaderAnim);
export const FestiveModeHeaderAnimReact = panel2react(`mp-festive-mode-header-anim`, FestiveModeHeaderAnim);
